import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { SPACE_CATEGORY_LABELS } from "@/lib/offer";
import { useI18n } from "@/contexts/I18nContext";

const SITE = "https://teamwork-africa.com";
const BRAND_ALIASES = ["Teamwork", "Team Work Africa", "Teamwork Africa", "Team Work Kinshasa", "Teamwork RDC", "Team Work Immobilier"];
const DEFAULT_KEYWORDS = "Team Work, Teamwork, Team Work Africa, Teamwork Africa, Team Work RDC, Team Work Kinshasa, immobilier Kinshasa, terrain Kinshasa, maison Kinshasa, automobile Kinshasa, location voiture Kinshasa, hôtel Kinshasa, appartement meublé Kinshasa, coworking Kinshasa, salle de mariage Kinshasa, salle de conférence Kinshasa, entreprise à vendre RDC, projet à financer RDC, investissement RDC, services à Kinshasa";

function upsertMeta(selector, attributes) {
  let element = document.head.querySelector(selector);
  if (!element) { element = document.createElement("meta"); document.head.appendChild(element); }
  Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
}

export default function SeoManager() {
  const location = useLocation();
  const { lang } = useI18n();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const category = params.get("category");
    const purpose = params.get("purpose");
    const sector = params.get("sector");
    const spaceLabel = SPACE_CATEGORY_LABELS[category];
    let title = "Team Work Africa | Immobilier, Auto, Séjours, Business et Services en RDC";
    let description = "Découvrez avec Team Work Africa des biens immobiliers, véhicules, hôtels, appartements meublés, espaces professionnels, entreprises, projets et services à Kinshasa et en RDC.";

    if (location.pathname === "/explorer") {
      if (spaceLabel) {
        title = `${spaceLabel} à louer à Kinshasa | Team Work`;
        description = `Trouvez ${spaceLabel.toLowerCase()} à Kinshasa avec prix, capacité, équipements et demande de disponibilité en ligne.`;
      } else if (sector === "auto") {
        title = `${purpose === "rent" ? "Location" : purpose === "sale" ? "Vente" : "Automobile"} de véhicules à Kinshasa | Team Work`;
        description = "Voitures, SUV, motos, camions et engins disponibles à Kinshasa avec Team Work.";
      } else if (sector === "real_estate") {
        title = `${purpose === "rent" ? "Location" : purpose === "sale" ? "Vente" : "Immobilier"} immobilière à Kinshasa | Team Work`;
        description = "Maisons, appartements, villas, terrains, bureaux et espaces professionnels à Kinshasa.";
      } else if (sector === "hospitality") {
        title = "Hôtels et appartements meublés à Kinshasa | Team Work";
        description = "Réservez un hôtel, une résidence ou un appartement meublé à Kinshasa avec disponibilités et capacités contrôlées.";
      } else title = "Offres et opportunités à Kinshasa | Team Work Africa";
    } else if (location.pathname === "/a-propos") {
      title = lang === "en" ? "About Team Work | Property, Automotive, Business & Projects in the DRC" : "À propos de Team Work | Immobilier, Automobile, Business & Projets en RDC";
      description = lang === "en" ? "Team Work connects owners, buyers, entrepreneurs and investors with property, automotive, professional and business opportunities in the DRC." : "Team Work connecte propriétaires, acheteurs, entrepreneurs et investisseurs à des opportunités immobilières, automobiles, professionnelles et entrepreneuriales en RDC.";
    } else if (location.pathname === "/contact") title = "Contacter Team Work à Kinshasa";

    const canonical = `${SITE}${location.pathname}${location.pathname === "/explorer" && spaceLabel ? `?sector=real_estate&purpose=rent&category=${category}` : ""}`;
    document.title = title;
    let link = document.head.querySelector('link[rel="canonical"]');
    if (!link) { link = document.createElement("link"); link.rel = "canonical"; document.head.appendChild(link); }
    link.href = canonical;
    upsertMeta('meta[name="description"]', { name: "description", content: description });
    upsertMeta('meta[name="keywords"]', { name: "keywords", content: DEFAULT_KEYWORDS });
    const privatePrefixes = ["/admin", "/connexion", "/compte", "/demandes", "/favoris"];
    upsertMeta('meta[name="robots"]', { name: "robots", content: privatePrefixes.some((prefix) => location.pathname.startsWith(prefix)) ? "noindex,nofollow" : "index,follow" });
    upsertMeta('meta[property="og:title"]', { property: "og:title", content: title });
    upsertMeta('meta[property="og:description"]', { property: "og:description", content: description });
    upsertMeta('meta[property="og:url"]', { property: "og:url", content: canonical });

    let script = document.head.querySelector('#teamwork-organization-schema');
    if (!script) { script = document.createElement("script"); script.type = "application/ld+json"; script.id = "teamwork-organization-schema"; document.head.appendChild(script); }
    script.textContent = JSON.stringify({
      "@context": "https://schema.org", "@graph": [
        {
          "@type": ["Organization", "RealEstateAgent"], "@id": `${SITE}/#organization`, name: "Team Work",
          alternateName: BRAND_ALIASES, url: SITE, logo: `${SITE}/logo-teamwork.png`, telephone: "+243981388707",
          email: "teamworkimmo243@gmail.com", address: { "@type": "PostalAddress", addressLocality: "Kinshasa", addressCountry: "CD" },
          areaServed: { "@type": "AdministrativeArea", name: "Kinshasa, République démocratique du Congo" },
          knowsAbout: ["Immobilier", "Automobile", "Hôtels et séjours", "Appartements meublés", "Espaces professionnels", "Événementiel", "Business et projets", "Investissement", "Déménagement", "Nettoyage", "Décoration", "Photographie et vidéo", "Traiteur", "Sécurité", "Internet", "Énergie solaire"],
        },
        {
          "@type": "WebSite", "@id": `${SITE}/#website`, name: "Team Work", alternateName: BRAND_ALIASES,
          url: SITE, publisher: { "@id": `${SITE}/#organization` }, inLanguage: "fr",
          potentialAction: { "@type": "SearchAction", target: `${SITE}/explorer?q={search_term_string}`, "query-input": "required name=search_term_string" },
        },
      ],
    });
  }, [location.pathname, location.search, lang]);

  return null;
}
